import React, { useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import S from './UrlFetch.module.scss'
import {
  API_SOURCES,
  buildApiUrl,
  detectApiSourceByUrl,
  loadUrlSourceData,
} from './apiSources'
import { buildUrlTabShareUrl } from '../../../services/urlState'

function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text)
  }
  const el = document.createElement('textarea')
  el.value = text
  el.style.cssText = 'position:fixed;top:-9999px;left:-9999px'
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
  return Promise.resolve()
}

function resolveParamDefault(p) {
  if (p.default === 'today') return new Date().toISOString().split('T')[0]
  return String(p.default ?? '')
}

export async function fetchData(source) {
  return loadUrlSourceData(source)
}

export default function UrlFetch({
  userInput,
  setUserInput,
  setLoadingError,
  initialState = null,
}) {
  const [connParams, setConnParams] = useState(initialState?.connParams ?? {})
  const [selectedPresetId, setSelectedPresetId] = useState(
    initialState?.apiSourceId ?? detectApiSourceByUrl(initialState?.url ?? '')?.id ?? null
  )
  const [url, setUrl] = useState(() => {
    if (initialState?.url) return initialState.url
    const presetId = initialState?.apiSourceId
    if (presetId) {
      const preset = API_SOURCES.find((s) => s.id === presetId)
      if (preset?.buildUrl) {
        const storedParams = initialState?.connParams?.[presetId] ?? {}
        const resolved = {}
        ;(preset.params ?? []).forEach((p) => {
          resolved[p.k] = storedParams[p.k] ?? resolveParamDefault(p)
        })
        return preset.buildUrl(resolved)
      }
      return preset?.defaultUrl ?? ''
    }
    return ''
  })
  const [apiKey, setApiKey] = useState(initialState?.apiKey ?? '')
  const [userDataPath, setUserDataPath] = useState(initialState?.userDataPath ?? '')
  const [loaded, setLoaded] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [copied, setCopied] = useState(false)
  const [urlCopied, setUrlCopied] = useState(false)

  const selectedPreset = useMemo(
    () => API_SOURCES.find((source) => source.id === selectedPresetId) ?? null,
    [selectedPresetId]
  )

  const getEffectiveParams = useCallback(
    (preset) => {
      const stored = connParams[preset.id] ?? {}
      const merged = {}
      ;(preset.params ?? []).forEach((p) => {
        merged[p.k] = stored[p.k] ?? resolveParamDefault(p)
      })
      return merged
    },
    [connParams]
  )

  const handleConnectorToggle = useCallback(
    (preset) => {
      if (selectedPresetId === preset.id) {
        setSelectedPresetId(null)
        setUrl('')
        setApiKey('')
        setUserDataPath('')
        setLoaded(false)
        return
      }
      setSelectedPresetId(preset.id)
      setApiKey('')
      setUserDataPath(preset.dataPath ?? '')
      setLoaded(false)
      const defaults = {}
      ;(preset.params ?? []).forEach((p) => { defaults[p.k] = resolveParamDefault(p) })
      const effectiveParams = { ...defaults, ...(connParams[preset.id] ?? {}) }
      setConnParams((prev) => ({ ...prev, [preset.id]: effectiveParams }))
      setUrl(
        preset.buildUrl ? preset.buildUrl(effectiveParams) : (preset.defaultUrl ?? '')
      )
    },
    [selectedPresetId, connParams]
  )

  const handleParamChange = useCallback(
    (preset, key, value) => {
      const nextStored = { ...(connParams[preset.id] ?? {}), [key]: value }
      setConnParams((prev) => ({ ...prev, [preset.id]: nextStored }))
      if (preset.buildUrl) {
        const allParams = {}
        ;(preset.params ?? []).forEach((p) => {
          allParams[p.k] = nextStored[p.k] ?? p.default ?? ''
        })
        setUrl(preset.buildUrl(allParams))
      }
    },
    [connParams]
  )

  const syncPresetFromUrl = useCallback((nextUrl) => {
    const detected = detectApiSourceByUrl(nextUrl)
    setSelectedPresetId(detected?.id ?? null)
  }, [])

  const fetchUrl = useCallback(
    async (inputUrl) => {
      const preset = selectedPreset ?? detectApiSourceByUrl(inputUrl)
      const finalUrl = buildApiUrl(
        { url: inputUrl, apiKey, apiSourceId: preset?.id },
        preset
      )
      const meta = preset?.buildMeta?.(preset ? getEffectiveParams(preset) : {})
      const source = {
        type: 'url',
        url: finalUrl,
        apiSourceId: preset?.id ?? null,
        apiKey: preset?.requiresKey ? apiKey : undefined,
        dataTypes: preset?.dataTypes,
        userDataPath: userDataPath.trim() || undefined,
        datasetTitle: meta?.title,
        datasetSrc: meta?.source,
      }
      setFetching(true)
      try {
        const data = await fetchData(source)
        setUserInput(data, source)
        setLoadingError(null)
        setLoaded(true)
      } catch (e) {
        setLoadingError('Loading error. ' + e.message)
        setLoaded(false)
      } finally {
        setFetching(false)
      }
    },
    [apiKey, selectedPreset, userDataPath, setLoadingError, setUserInput, getEffectiveParams]
  )

  const handleSubmit = useCallback(
    (e) => {
      e.stopPropagation()
      e.preventDefault()
      fetchUrl(url)
      return false
    },
    [fetchUrl, url]
  )

  const selectedPresetNeedsKey = selectedPreset?.requiresKey ?? false

  const connectorIcon = (preset) => (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="22"
      height="22"
      dangerouslySetInnerHTML={{ __html: preset.iconPaths ?? '' }}
    />
  )

  const loadBtnContent = fetching
    ? <><span className={S.spinner} aria-hidden="true" />Loading…</>
    : loaded ? 'Reload data →' : 'Load data →'

  return (
    <form onSubmit={handleSubmit} className={S['url-fetch']}>

      {/* URL zone — switches between plain input and URL preview */}
      <div className={S['url-field']}>
        <div className={S['url-label-row']}>
          <span className={S['url-label']}>
            {selectedPreset ? (
              <>Request URL <span className={S['url-preview-tag']}>Preview</span></>
            ) : 'Data URL'}
          </span>
          <button
            type="button"
            className={S['url-share-btn']}
            onClick={() => {
              copyToClipboard(buildUrlTabShareUrl()).then(() => {
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
              })
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
            {copied ? 'Copied!' : 'Share'}
          </button>
        </div>

        {selectedPreset ? (
          <div className={S['url-preview-box']}>
            <span className={S['url-preview-text']}>{url}</span>
            <button
              type="button"
              className={S['url-copy-icon']}
              title="Copy URL"
              onClick={() => {
                copyToClipboard(url).then(() => {
                  setUrlCopied(true)
                  setTimeout(() => setUrlCopied(false), 2000)
                })
              }}
            >
              {urlCopied
                ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>
              }
            </button>
          </div>
        ) : (
          <div className={S['url-input-row']}>
            <input
              className={S['url-input']}
              value={url}
              onChange={(e) => {
                const nextUrl = e.target.value
                setUrl(nextUrl)
                syncPresetFromUrl(nextUrl)
                setLoaded(false)
              }}
              placeholder="Paste a public API or file URL"
            />
            <button
              className={classNames(S['load-btn'], loaded && !fetching && S['load-btn-reload'])}
              disabled={!url || fetching}
              type="submit"
            >
              {fetching
                ? <><span className={S.spinner} aria-hidden="true" />Loading…</>
                : loaded ? 'Reload' : 'Load data'}
            </button>
          </div>
        )}

        {loaded && !selectedPreset && (
          <div className={S['url-success']}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
            Loaded — scroll down to review, then hit Next
          </div>
        )}

        <p className={S.hint}>
          {selectedPreset ? (
            <>
              Built from <b>{selectedPreset.label}</b> — edit the fields below and this stays in sync.{' '}
              <button
                type="button"
                className={S['url-own-link']}
                onClick={() => handleConnectorToggle(selectedPreset)}
              >
                Load your own URL instead
              </button>
            </>
          ) : (
            'Pulls a CSV, TSV or JSON file from a public link, or pick a live source below to build the URL for you.'
          )}
        </p>
      </div>

      {/* Live sources divider */}
      <div className={S['conn-divider']}>
        <span>Live data sources</span>
      </div>

      {/* Connector list */}
      <div className={S['conn-list']}>
        {API_SOURCES.map((preset) => {
          const isSelected = selectedPreset?.id === preset.id

          if (isSelected) {
            const effectiveParams = getEffectiveParams(preset)
            return (
              <div key={preset.id} className={S.cfg}>
                {/* Header */}
                <div className={S['cfg-head']}>
                  <span className={S['conn-ico']}>
                    {connectorIcon(preset)}
                  </span>
                  <span className={S['conn-main']}>
                    <span className={S['conn-title']}>
                      {preset.description}
                      {preset.badge && <span className={S['badge-new']}>{preset.badge}</span>}
                    </span>
                    <span className={S['conn-sub']}>{preset.label} · {preset.fmt ?? 'JSON'}</span>
                  </span>
                  <button
                    type="button"
                    className={S['cfg-close']}
                    onClick={() => handleConnectorToggle(preset)}
                    aria-label="Close configurator"
                  >
                    ×
                  </button>
                </div>

                {/* Body */}
                <div className={S['cfg-body']}>
                  {preset.params?.length > 0 && (
                    <div className={S['cfg-grid']}>
                      {preset.params.map((param) => (
                        <div key={param.k} className={S['cfg-field']}>
                          <label>{param.label}</label>
                          {param.type === 'select' ? (
                            <select
                              value={effectiveParams[param.k]}
                              onChange={(e) => handleParamChange(preset, param.k, e.target.value)}
                            >
                              {(param.opts ?? []).map((opt) => {
                                const val = typeof opt === 'string' ? opt : opt.value
                                const lbl = typeof opt === 'string' ? opt : opt.label
                                return <option key={val} value={val}>{lbl}</option>
                              })}
                            </select>
                          ) : (
                            <input
                              type={param.type === 'date' ? 'date' : param.type === 'number' ? 'number' : 'text'}
                              value={effectiveParams[param.k]}
                              onChange={(e) => handleParamChange(preset, param.k, e.target.value)}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className={S['cfg-field']}>
                    <label>JSON data path</label>
                    <input
                      type="text"
                      value={userDataPath}
                      onChange={(e) => setUserDataPath(e.target.value)}
                      placeholder={preset.dataPath ? `Default: ${preset.dataPath}` : 'e.g. features, [1], data.rows'}
                    />
                  </div>

                  {preset.requiresKey && (
                    <div className={S['cfg-field']}>
                      <span className={S['cfg-key-label']}>
                        <label>{preset.keyLabel ?? 'API key'}</label>
                        {preset.keyDocs && (
                          <a href={preset.keyDocs} target="_blank" rel="noopener noreferrer">
                            Get a free key ↗
                          </a>
                        )}
                      </span>
                      <input
                        type="password"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder={`Paste your ${preset.label} key`}
                        className={S['cfg-key-input']}
                      />
                    </div>
                  )}

                  <button
                    className={S['load-primary']}
                    type="submit"
                    disabled={(selectedPresetNeedsKey && !apiKey.trim()) || fetching}
                  >
                    {loadBtnContent}
                  </button>

                  {loaded && (
                    <div className={S['url-success']}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                      Loaded — review the data, then hit Next
                    </div>
                  )}
                </div>
              </div>
            )
          }

          return (
            <button
              key={preset.id}
              type="button"
              className={S.conn}
              onClick={() => handleConnectorToggle(preset)}
            >
              <span className={S['conn-ico']}>
                {connectorIcon(preset)}
              </span>
              <span className={S['conn-main']}>
                <span className={S['conn-title']}>
                  {preset.description}
                  {preset.badge && <span className={S['badge-new']}>{preset.badge}</span>}
                </span>
                <span className={S['conn-sub']}>{preset.label} · {preset.fmt ?? 'JSON'}</span>
              </span>
              <span className={S['conn-right']}>
                <span className={classNames(S.pill, preset.requiresKey ? S['pill-key'] : S['pill-free'])}>
                  {preset.requiresKey ? 'API key' : 'No key needed'}
                </span>
                <svg className={S['conn-arr']} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </span>
            </button>
          )
        })}
      </div>

      {/* Supported formats */}
      <div className={S.formats}>
        <span className={S['formats-label']}>Supported formats</span>
        {['CSV', 'TSV', 'DSV', 'JSON'].map((f) => (
          <span key={f} className={S.fmt}>{f}</span>
        ))}
      </div>

    </form>
  )
}
